import { lazy } from "react";

// A stale deploy leaves old tabs asking for chunk hashes that no longer exist on
// the CDN. One reload fetches the fresh index.html + chunk map and fixes it.
// We record when the last recovery reload happened (not a boolean) so that:
//   - a successful chunk load clears it, giving later failures a fresh attempt
//   - a chunk that is genuinely gone can't trap the tab in a reload loop
const RELOAD_KEY = "chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10000;

export const lazyWithRetry = (importFn) =>
  lazy(() =>
    importFn().then(
      (mod) => {
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      },
      () => {
        const lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        const canReload =
          !lastReloadAt || Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS;

        if (canReload) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Keep the Suspense boundary pending until the reload actually lands.
          // Resolving here with a placeholder mounts a replacement tree while
          // the document is being torn down, which races react-dom's removeChild
          // and throws NotFoundError ("The object can not be found here") on iOS
          // Chrome. A never-resolving promise keeps React out of the DOM until
          // the browser finishes navigating.
          return new Promise(() => {});
        }

        // Reload already tried moments ago: retry the import once in case the
        // chunk came back, otherwise let the error boundary handle it.
        return importFn().catch((err) => {
          sessionStorage.removeItem(RELOAD_KEY);
          throw err;
        });
      }
    )
  );
